import PageContainer from "../../PageContainer";
import { useData } from "../../../../contexts/DataContext/DataContextt";
import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { RowActions } from "../../../../utils/RowActions";
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
  Button,
  Divider,
  Chip,
} from "@mui/material";

import ClassCard from "../ClassCard";
import RefreshIcon from "@mui/icons-material/Refresh";
import TabulatedGridCollapsibleV2 from "../../TabulatedGridCollapsibleV2";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import AddIncludedFeatureAutoComplete from "./AddIncludedFeatureAutoComplete";
import { usePreparationData } from "../../../../contexts/hooks/usePreparationData";
import { fetchFeatures } from "../../../../services/featureService";
import { useSearch } from "../../../../contexts/hooks/useReusableHook";
import usePageIncludedFeature from "../../../../contexts/hooks/usePageIncludedFeature";
import { useUnsavedChangesGuard } from "../../../../contexts/hooks/useUnsavedChangesGuard";
import { usePreload } from "../../../../contexts/DataContext/PreloadContext";
import { deleteIncludedFeature } from "../../../../services/includedFeatureService";
import { PageSessionKey } from "../../../../types/constant";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";

export default function IncludedFeaturesPage() {
  const { id } = useParams();
  const preloadSources = ["features"];
  const { language } = useData();
  const navigate = useNavigate();
  const [showValues, setShowValues] = useState(true);
  const { confirmDialog, showNotification } = usePreload();

  const {
    reload,
    selectedClass,
    searchTerm,
    isLoading,
    loadError,
    currentIncludedFeature,
    filteredIncludedFeatures,
    handleFeatureChange,
    handleSearchClick,
    handleKeyDown,
    removeFromCache,
    handleSaveNewFeatures,
  } = usePageIncludedFeature({
    id: id,
    language,
    deps: [id, language],
  });

  // count from the unfiltered list so a search can't hide unsaved rows
  const allIncludedFeatures =
    currentIncludedFeature?.selectedIncludedFeatures ?? [];
  const newCount = allIncludedFeatures.filter((f) => f.isSelectionNew).length;
  // unsaved rows live in includedFeaturePageCache, so in-app navigation keeps
  // them; only a tab close/reload or the Refresh button (confirmLeave) drops them
  const { confirmLeave } = useUnsavedChangesGuard(newCount > 0, {
    interceptLinks: false,
  });

  const { featureOptions } = usePreparationData({
    sources: preloadSources,
  });

  const {
    options: featureLocalOptions,
    search: searchFeatures,
    searching: isFeatureSearching,
  } = useSearch(featureOptions, fetchFeatures, {
    displayNameFields: ["featureDisplayName", "featureName"],
    extraKeys: [
      "featureDisplayName",
      "featureName",
      "defaultValues",
      "defaultUnit",
      "featureType",
    ],
  });

  const headers = [
    {
      prop: "includedFeatureIndexOrder",
      text: "",
      minWidth: 5,
      width: 1,
    },
    {
      prop: "includedFeatureFeatureId",
      text: "Code",
      width: 100,
      minWidth: 50,
      link: {
        path: `/feature/edit`,
        action: (row) => {
          sessionStorage.setItem(
            PageSessionKey.EDIT_FEATURE,
            JSON.stringify(row.feature?.[0]),
          );
          window.open(
            `/feature/edit/${row.includedFeatureFeatureId}`,
            "_blank",
          );
        },
      },
    },
    { prop: "featureDisplayName", text: "Name", minWidth: 50 },
    {
      prop: "featureSelectable",
      text: "Selectable",
      minWidth: 50,
      isSelectabale: true,
    },

    { prop: "featureType", text: "Type", width: 50, minWidth: 50 },
    {
      prop: ["featureIsActive"],
      text: "Status",
      isStatus: true,
      width: 100,
      minWidth: 10,
    },
    {
      prop: "featureImage",
      text: "Symbols",
      width: 10,
      minWidth: 10,
      isImage: true,
    },
    {
      prop: "",
      text: "Unit",
      isUnit: true,
      minWidth: 10,
      width: 10,
    },
    {
      prop: "",
      text: "Unit (imp.)",
      isUnitImperial: true,
      minWidth: 100,
      width: 100,
    },
    {
      prop: "",
      text: "Value Code - Description",
      isValueCodes: true,
      showValues: showValues,
      minWidth: 240,
    },
  ];

  const handleIncludedFeatureDelete = async (row) => {
    const ok = await confirmDialog.show({
      title: "Remove Included Feature",
      message: `Are you sure you want to remove ${
        row?.featureDisplayName || row?.includedFeatureFeatureId
      } (${row?.includedFeatureFeatureId}) from this class?`,
    });

    if (!ok) return;

    try {
      confirmDialog.setLoading(true);
      if (!row?.isSelectionNew) await deleteIncludedFeature(row);

      removeFromCache(row.includedFeatureUniqueId);
      showNotification("Deleted successfully", "success");
    } catch (err) {
      console.error(err);
      showNotification("Failed to remove feature", "error");
    } finally {
      confirmDialog.close();
    }
  };

  const pageRowActions = () => [
    {
      tooltip: "Edit Included Feature",
      icon: <EditNoteRoundedIcon />,
      onClick: (row) => {
        navigate(
          `/classes/${id}/includedfeature/edit/${row.includedFeatureFeatureId}`,
          {
            state: { row: row },
          },
        );
      },
    },
    {
      tooltip: "Remove Feature From Class",
      icon: <DeleteForeverRoundedIcon />,
      color: "error",
      onClick: (row) => {
        handleIncludedFeatureDelete(row);
      },
    },
  ];

  return (
    <PageContainer
      title="Included Features"
      subtitle="Manage the ETIM features included in this class"
      breadcrumbs={[
        { title: "ETIM Class", path: "/classNew" },
        { title: `${id}` },
      ]}
      actions={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip
            title="Refresh included features"
            placement="bottom"
            enterDelay={300}
          >
            <div>
              <IconButton
                size="small"
                aria-label="Refresh included features"
                onClick={async () => {
                  if (await confirmLeave()) reload(true);
                }}
              >
                <RefreshIcon />
              </IconButton>
            </div>
          </Tooltip>
          <Tooltip title="Back" placement="bottom" enterDelay={300}>
            <div>
              <IconButton
                size="small"
                aria-label="Back to classes"
                component={Link}
                to="/classNew"
              >
                <KeyboardBackspaceIcon />
              </IconButton>
            </div>
          </Tooltip>
        </Stack>
      }
    >
      <Box mt={1}>
        <ClassCard
          selectedClass={selectedClass}
          loading={isLoading}
          notFoundCode={id}
          loadFailed={loadError}
        />
      </Box>

      <Box mt={4} display={"flex"} flexDirection={"column"}>
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          alignItems="center"
          justifyContent={"space-between"}
          sx={{ width: "100%" }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6" component="div">
              Features
            </Typography>
            <Chip
              size="small"
              label={allIncludedFeatures.length}
              aria-label={`${allIncludedFeatures.length} features`}
            />
            {newCount > 0 && (
              <Chip
                size="small"
                color="warning"
                label={`${newCount} unsaved`}
              />
            )}
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            alignItems="center"
          >
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showValues}
                  onChange={() => setShowValues(!showValues)}
                  name="showHideValues"
                  color="info"
                  inputProps={{ "aria-label": "Show values" }}
                />
              }
              label="Show values"
            />
            <Divider flexItem orientation="vertical" />
            <AddIncludedFeatureAutoComplete
              disabled={!selectedClass}
              label="Add Features"
              options={featureLocalOptions}
              onChange={(value) => {}}
              defaultValue={allIncludedFeatures.map((m) => ({
                id: m.includedFeatureFeatureId,
                isSelectionNew: m.isSelectionNew ?? false,
                displayName: m.featureDisplayName ?? "",
              }))}
              onSearch={searchFeatures}
              searching={isFeatureSearching}
              callBack={handleFeatureChange}
            />
            {newCount > 0 && (
              <Button
                variant="contained"
                size="small"
                startIcon={<SaveRoundedIcon />}
                onClick={handleSaveNewFeatures}
              >
                {`Save ${newCount} feature${newCount > 1 ? "s" : ""}`}
              </Button>
            )}
          </Stack>
        </Stack>
        <Box mt={2}>
          <TabulatedGridCollapsibleV2
            variant="modern"
            loading={isLoading}
            emptyState={
              <Stack
                alignItems="center"
                spacing={1.5}
                sx={{ py: 8, color: "text.secondary" }}
              >
                <SearchOffRoundedIcon sx={{ fontSize: 40 }} />
                {searchTerm ? (
                  <>
                    <Typography>No features match</Typography>
                    <Button size="small" onClick={() => handleSearchClick("")}>
                      Clear search
                    </Button>
                  </>
                ) : (
                  <>
                    <Typography>No features included yet</Typography>
                    {selectedClass && (
                      <Typography variant="body2">
                        Use Add Features to include features in this class.
                      </Typography>
                    )}
                  </>
                )}
              </Stack>
            }
            search={{
              placeHolder: "Search included feature...",
              handleSearchClick: handleSearchClick,
              onKeyDown: handleKeyDown,
              activeTerm: searchTerm,
              onClear: () => handleSearchClick(""),
            }}
            headers={headers}
            data={filteredIncludedFeatures}
            rowActions={(row) => (
              <RowActions row={row} actions={pageRowActions()} />
            )}
          />
        </Box>
      </Box>
    </PageContainer>
  );
}
